import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IActivo,
  IFilter,
  IGeoJSONLineString,
  IGeoJSONPoint,
  IParada,
  IQueryParam,
  IReporteBase,
  IReporteGenerico,
  ITipoParada,
  IUbicacion,
} from 'modelos/src';
import { FormGroup } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  CreadorPolygon,
  Parada,
} from '../../../../../auxiliares/mapa-creador-mapbox/mapa-creador-mapbox.component';
import { firstValueFrom, Subscription } from 'rxjs';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { DateRange } from '../../../../../auxiliares/date-range-selector/date-range-selector.component';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

@Component({
  selector: 'app-mapa-paradas-mapbox',
  templateUrl: './mapa-paradas-mapbox.component.html',
  styleUrl: './mapa-paradas-mapbox.component.scss',
  standalone: false,
})
export class MapaParadasMapboxComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  private helper = inject(HelperService);

  @Input() formulario?: FormGroup;
  @Input() terminales?: IUbicacion[];

  public lineString: [number, number][] = [];
  public points: Parada[] = [];
  public paradas: IParada[] = [];
  public indicaciones = [
    'Para dibujar un recorrido presioná el botón de las líneas.',
    'Para finalizar el recorrido hacé doble clic.',
    'Para agregar paradas hacé clic en el mapa con el recorrido en pausa.',
  ];
  public tipoParada: ITipoParada[] = ['parada', 'refugio'];

  public terminalesMapa: CreadorPolygon[] = [];

  // Colectivos y cosas para importar por reporte
  public colectivos: IActivo[] = [];
  public colectivo?: IActivo;
  public reportes: IReporteBase<'Tracker 4G'>[] = [];
  private colectivos$?: Subscription;
  private reportes$?: Subscription;
  public dateRange: DateRange = {
    start: new Date(
      new Date().setHours(new Date().getHours() - 3),
    ).toISOString(),
    end: new Date().toISOString(),
  };

  public ayer = this.helper.ayer;
  public hasta = 0;
  public importar = false;
  public min = 0;
  public max?: number;

  /// Drag and Drop
  public drop(event: CdkDragDrop<IParada[]>) {
    moveItemInArray(
      event.container.data,
      event.previousIndex,
      event.currentIndex,
    );
    this.renombrarParadas();
    // No necesitamos reconstruir points aquí, ya están correctos
  }

  private renombrarParadas() {
    this.paradas.forEach((p, idx) => {
      if (p.nombre?.includes('Parada')) {
        p.nombre = `Parada #${idx + 1}`;
      }
    });
  }

  public removeParada(p: IParada) {
    event?.stopPropagation();
    const index = this.paradas.indexOf(p);
    if (index > -1) {
      this.paradas.splice(index, 1);
      // Crear nuevo array para que Angular detecte el cambio
      this.points = this.points.filter((_, idx) => idx !== index);
    }
    this.renombrarParadas();
    this.formulario?.patchValue({ paradas: this.paradas });
  }

  /////

  public onLineStringChange(e: [number, number][]) {
    this.lineString = e;

    // Crear el objeto GeoJSON
    const geojson: IGeoJSONLineString = {
      type: 'LineString',
      coordinates: e,
    };
    this.formulario?.patchValue({ geojson });
  }

  public onPointsChange(e: Parada[]) {
    this.points = e;
    this.generarParadas();
    this.formulario?.patchValue({ paradas: this.paradas });
  }

  private generarParadas() {
    const paradasViejas = this.paradas;
    this.paradas = this.points.map((p, idx) => {
      // Buscar parada anterior por índice (más confiable que coordenadas)
      const paradaAnterior = paradasViejas[idx];

      const nombre = paradaAnterior?.nombre || p.nombre || `Parada #${idx + 1}`;
      const destino =
        paradaAnterior?.destino ||
        paradasViejas[idx - 1]?.destino ||
        this.formulario?.get('destino')?.value;
      const por =
        paradaAnterior?.por ||
        paradasViejas[idx - 1]?.por ||
        this.formulario?.get('por')?.value;

      const parada: IParada = {
        // Usar GeoJSON directamente - Mapbox ya usa [lng, lat]
        geojson: {
          type: 'Point',
          coordinates: p.ubicacion,
        },
        nombre,
        destino,
        por,
        bajada: paradaAnterior?.bajada ?? true,
        subida: paradaAnterior?.subida ?? true,
        tipo: p.tipo || paradaAnterior?.tipo || 'parada',
      };

      return parada;
    });
  }

  // ng-select emite el tipo elegido
  public onTipoParadaChange(value: ITipoParada, id: string) {
    const index = this.paradas.findIndex((p) => p._id === id);
    if (index !== -1) {
      this.paradas[index].tipo = value;
      this.points[index].tipo = value;
      this.formulario?.patchValue({ paradas: this.paradas });
    }
  }

  public async listar(): Promise<void> {
    const query: IQueryParam = {
      select: 'identificacion',
      includeChildren: true,
    };
    this.colectivos$?.unsubscribe();
    this.colectivos$ = this.lista2.colectivos(query, 30).subscribe((data) => {
      this.colectivos = data.datos;
    });
  }

  public async listarReportes(): Promise<void> {
    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: this.colectivo?._id,
      'valores.timestamp': {
        $gte: this.dateRange?.start,
        $lte: this.dateRange?.end,
      },
    } as any;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'geojson fechaDevice',
      sort: '-valores.timestamp',
      includeChildren: true,
    };
    const res = await firstValueFrom(
      this.lista2.reportesGenericos<'Tracker 4G'>(['Tracker 4G'], query),
    );
    this.reportes = res.datos;
    this.max = res.datos?.length > 0 ? res.datos?.length - 1 : 0;
  }

  public async onDateRangeChange() {
    const res = await this.dialogService.confirm(
      `¿Querés importar los reportes de: ${this.colectivo.identificacion}`,
      'Si importa los resportes se borrará el recorrido y las paradas.',
    );

    if (!res) return;
    this.lineString = [];
    this.paradas = [];
    this.points = [];
    await this.listarReportes();
    const lineString = this.reportes.map((r) => {
      const valores = this.helper.getValores(r);
      // Mapbox usa [lon, lat] nativo (sin conversión)
      const geojson = valores.geojson as IGeoJSONPoint;
      return geojson.coordinates as [number, number];
    });
    this.lineString = lineString;
  }

  public async importarRecorrido() {
    const res = await this.dialogService.confirm(
      `¿Querés importar los reportes de: ${this.colectivo.identificacion}`,
      'Si importa los resportes se borrará el recorrido y las paradas.',
    );

    if (!res) return;

    this.lineString = [];
    this.paradas = [];
    this.points = [];
    await this.listarReportes();
    const lineString = this.reportes.map((r) => {
      const valores = this.helper.getValores(r);
      // Mapbox usa [lon, lat] nativo (sin conversión)
      const geojson = valores.geojson as IGeoJSONPoint;
      return geojson.coordinates as [number, number];
    });
    this.lineString = lineString;
  }

  public async onHastaChange() {
    /// Le sumo al start el valor del input
    const fecha = new Date(this.dateRange.start);
    fecha.setHours(fecha.getHours() + this.hasta);
    this.dateRange.end = fecha.toISOString();
  }

  public onChangeSlider() {
    // Particiono los reportes con el min y el max.
    const reportes = this.reportes.slice(this.min, this.max);
    const reportesMapbox = reportes.map((r) => {
      const valores = this.helper.getValores(r);
      // Mapbox usa [lon, lat] nativo (sin conversión)
      const geojson = valores.geojson as IGeoJSONPoint;
      return geojson.coordinates as [number, number];
    });
    this.lineString = reportesMapbox;
  }

  async ngOnInit() {
    await this.listar();
    // Obtener coordenadas desde geojson si existe
    const geojson = this.formulario?.get('geojson')?.value;
    this.lineString = geojson?.coordinates || []; // Ya en EPSG:4326

    this.paradas = this.formulario?.get('paradas')?.value || [];
    // Usar GeoJSON directamente - ya está en formato [lng, lat]
    this.points = this.paradas.map((p) => ({
      nombre: p.nombre,
      ubicacion: p.geojson?.coordinates || [0, 0],
      tipo: p.tipo || 'parada',
    })) as Parada[];
  }

  ngOnDestroy() {
    this.colectivos$?.unsubscribe();
    this.reportes$?.unsubscribe();
  }
}
